import { Col, Row, message } from 'antd';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PIPELINE_STEP_EXAMPLES } from './pipeline-examples';
import { PipelineDebugHeader } from './components/PipelineDebugHeader/PipelineDebugHeader';
import { PipelineFlowCard } from './components/PipelineFlowCard/PipelineFlowCard';
import { PipelineSetupCard } from './components/PipelineSetupCard/PipelineSetupCard';
import { usePipelineDebug } from './hooks/usePipelineDebug';
import { usePipelineUploadColumns } from './hooks/usePipelineUploadColumns';
import { completedStepCount } from './utils/step-display';
import { copyJsonToClipboard, downloadJsonFile } from './utils';

export interface PipelineDebugProps {
  organizationId: string;
  assetId: string;
  isSuperAdmin: boolean;
  organizations: Array<{ id: string; name: string }>;
  onOrganizationIdChange: (organizationId: string) => void;
  assets: Array<{ id: string; name: string }>;
  onAssetIdChange: (assetId: string) => void;
  isLoadingAssets: boolean;
}

export function PipelineDebug({
  organizationId,
  assetId,
  isSuperAdmin,
  organizations,
  onOrganizationIdChange,
  assets,
  onAssetIdChange,
  isLoadingAssets,
}: PipelineDebugProps) {
  const { t } = useTranslation('pipelineDebug');
  const {
    runMode,
    setRunMode,
    activeStepIndex,
    setActiveStepIndex,
    activeStepId,
    activeStep,
    uploads,
    steps,
    canRun,
    ingestFiles,
    ingestStepInputFiles,
    runFromActiveStep,
    continuePipeline,
    updateStepInput,
    updateStepOutput,
    downloadStepInput,
    downloadStepOutput,
    copyStepInput,
    copyStepOutput,
  } = usePipelineDebug({ organizationId, assetId });
  const { columns } = usePipelineUploadColumns();
  const examples = PIPELINE_STEP_EXAMPLES[activeStepId];

  const completedSteps = useMemo(() => completedStepCount(steps), [steps]);

  const copyExample = useCallback(
    async (value: unknown) => {
      const copied = await copyJsonToClipboard(value);
      if (copied) {
        message.success(t('messages.copied'));
      }
    },
    [t],
  );

  const downloadExample = useCallback((fileName: string, value: unknown) => {
    downloadJsonFile(fileName, value);
  }, []);

  return (
    <div className="app-page pipeline-debug-page">
      <PipelineDebugHeader uploads={uploads} completedSteps={completedSteps} />

      <Row gutter={[16, 16]} align="top">
        <Col xs={24} lg={8}>
          <PipelineSetupCard
            organizationId={organizationId}
            assetId={assetId}
            isSuperAdmin={isSuperAdmin}
            organizations={organizations}
            onOrganizationIdChange={onOrganizationIdChange}
            assets={assets}
            onAssetIdChange={onAssetIdChange}
            isLoadingAssets={isLoadingAssets}
            runMode={runMode}
            onRunModeChange={setRunMode}
            uploads={uploads}
            columns={columns}
            onFilesSelected={(files) => {
              void ingestFiles(files);
            }}
          />
        </Col>
        <Col xs={24} lg={16}>
          <PipelineFlowCard
            runMode={runMode}
            activeStepIndex={activeStepIndex}
            activeStepId={activeStepId}
            activeStep={activeStep}
            steps={steps}
            canRun={canRun}
            exampleInput={examples.input}
            exampleOutput={examples.output}
            onStepSelect={setActiveStepIndex}
            onRun={() => void runFromActiveStep()}
            onContinue={() => void continuePipeline()}
            onUpdateInput={(value) => updateStepInput(activeStepId, value)}
            onUpdateOutput={(value) => updateStepOutput(activeStepId, value)}
            onCopyInput={() => copyStepInput(activeStepId)}
            onCopyOutput={() => copyStepOutput(activeStepId)}
            onDownloadInput={() => downloadStepInput(activeStepId)}
            onDownloadOutput={() => downloadStepOutput(activeStepId)}
            onCopyExampleInput={() => {
              void copyExample(examples.input);
            }}
            onCopyExampleOutput={() => {
              void copyExample(examples.output);
            }}
            onDownloadExampleInput={() =>
              downloadExample(
                `${activeStepId}-example-input.json`,
                examples.input,
              )
            }
            onDownloadExampleOutput={() =>
              downloadExample(
                `${activeStepId}-example-output.json`,
                examples.output,
              )
            }
            onStepInputFilesSelected={(files) => {
              void ingestStepInputFiles(activeStepId, files);
            }}
            isUploadDisabled={!organizationId || !assetId}
          />
        </Col>
      </Row>
    </div>
  );
}
